/**
 * Reviews API Client
 *
 * HTTP REST API endpoints for Reviews functionality:
 * - Create review
 * - Get user reviews
 * - Get review stats
 * - Report review
 *
 * Uses HTTP-only cookies for authentication (via fetchWithTokenRefresh)
 */

import { buildApiUrl } from './config';
import { fetchWithTokenRefresh } from './refreshToken';
import { handleApiError } from './errorHandler';

// Review types
export type ReviewStatus = 'PENDING_MODERATION' | 'APPROVED' | 'REJECTED' | 'SUSPENDED';

export interface ContractorRatings {
  quality: number; // 1-5
  professionalism: number; // 1-5
  communication: number; // 1-5
  value: number; // 1-5
}

export interface ClientRatings {
  communication: number; // 1-5
  professionalism: number; // 1-5
  payment: number; // 1-5
}

export interface CreateReviewRequest {
  orderId: string;
  criteriaRatings: ContractorRatings | ClientRatings;
  comment?: string; // Max 1000 characters
}

export interface ReviewResponse {
  id: string;
  orderId: string;
  reviewerActorId: string;
  revieweeActorId: string;
  criteriaRatings: ContractorRatings | ClientRatings;
  comment?: string;
  averageRating: number;
  status: ReviewStatus;
  isVisible: boolean;
  isEdited?: boolean; // Whether the review has been edited
  editedAt?: string; // When the review was last edited
  createdAt: string;
  updatedAt: string;
  reviewerActor?: {
    id: string;
    name: string;
    displayName?: string;
    avatarUrl?: string;
  };
  revieweeActor?: {
    id: string;
    name: string;
    displayName?: string;
    avatarUrl?: string;
  };
  order?: {
    id: string;
    title: string;
  };
  response?: {
    content: string;
    createdAt: string;
  };
}

export interface RatingStats {
  averageRating: number;
  totalReviews: number;
  distribution: {
    [key: number]: number; // Star rating -> count
  };
  badges: string[];
  weightedScore: number;
  criteriaAverages?: {
    [key: string]: number;
  };
}

/**
 * Check if review can be edited (within 48 hours of creation)
 */
export function canEditReview(review: ReviewResponse): boolean {
  const hoursSinceCreation = (Date.now() - new Date(review.createdAt).getTime()) / (1000 * 60 * 60);
  return hoursSinceCreation < 48;
}

/**
 * Reviews API
 */
export const reviewsApi = {
  /**
   * Create new review
   * POST /reviews
   */
  async create(data: CreateReviewRequest): Promise<ReviewResponse> {
    const url = buildApiUrl('/reviews');

    const response = await fetchWithTokenRefresh(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      throw await handleApiError(response, 'Failed to create review');
    }

    const result = await response.json();
    return result;
  },

  /**
   * Get reviews for a user (reviews about the user)
   * GET /reviews/user/:userId
   */
  async getUserReviews(
    userId: string,
    page: number = 1,
    limit: number = 20,
    includeHidden?: boolean
  ): Promise<{
    data: ReviewResponse[];
    meta: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
  }> {
    const queryParams = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });

    if (includeHidden !== undefined) {
      queryParams.append('includeHidden', includeHidden.toString());
    }

    const url = buildApiUrl(`/reviews/user/${userId}?${queryParams.toString()}`);
    const response = await fetchWithTokenRefresh(url);

    if (!response.ok) {
      throw await handleApiError(response, 'Failed to fetch reviews');
    }

    return response.json();
  },


  /**
   * Update existing review
   * PATCH /reviews/:id
   */
  async update(reviewId: string, data: CreateReviewRequest): Promise<ReviewResponse> {
    const url = buildApiUrl(`/reviews/${reviewId}`);

    const response = await fetchWithTokenRefresh(url, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      throw await handleApiError(response, 'Failed to update review');
    }

    const result = await response.json();
    return result;
  },

  /**
   * Get review by ID
   * GET /reviews/:id
   */
  async getById(reviewId: string): Promise<ReviewResponse> {
    const url = buildApiUrl(`/reviews/${reviewId}`);
    const response = await fetchWithTokenRefresh(url);

    if (!response.ok) {
      throw await handleApiError(response, 'Failed to fetch review');
    }

    return response.json();
  },

  /**
   * Get reviews for a specific order
   * GET /reviews/order/:orderId
   */
  async getOrderReviews(orderId: string): Promise<{
    reviews: ReviewResponse[];
    order: {
      id: string;
      title: string;
      status: string;
      completedAt: string | null;
      clientActor: {
        id: string;
        userId: string;
        name: string;
        displayName?: string;
        avatarUrl?: string;
      } | null;
      workerActor: {
        id: string;
        userId: string;
        name: string;
        displayName?: string;
        avatarUrl?: string;
      } | null;
    };
    canReview: boolean;
  }> {
    const url = buildApiUrl(`/reviews/order/${orderId}`);
    const response = await fetchWithTokenRefresh(url);

    if (!response.ok) {
      throw await handleApiError(response, 'Failed to fetch order reviews');
    }

    return response.json();
  },

  /**
   * Get rating statistics for a user
   * GET /reviews/stats/:userId
   */
  async getUserStats(userId: string): Promise<RatingStats> {
    const url = buildApiUrl(`/reviews/stats/${userId}`);
    const response = await fetchWithTokenRefresh(url);

    if (!response.ok) {
      throw await handleApiError(response, 'Failed to fetch rating stats');
    }

    return response.json();
  },

  /**
   * Create response to a review
   * POST /reviews/:id/response
   */
  async createResponse(reviewId: string, content: string): Promise<ReviewResponse> {
    const url = buildApiUrl(`/reviews/${reviewId}/response`);
    const response = await fetchWithTokenRefresh(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ content }),
    });

    if (!response.ok) {
      throw await handleApiError(response, 'Failed to create response');
    }

    return response.json();
  },

  /**
   * Report a review
   * POST /reviews/:id/report
   */
  async report(
    reviewId: string,
    reason: string,
    description?: string
  ): Promise<{ message: string }> {
    const url = buildApiUrl(`/reviews/${reviewId}/report`);
    const response = await fetchWithTokenRefresh(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ reason, description }),
    });

    if (!response.ok) {
      throw await handleApiError(response, 'Failed to report review');
    }

    return response.json();
  },

  /**
   * Delete review (soft delete)
   * DELETE /reviews/:id
   */
  async delete(reviewId: string): Promise<{ message: string }> {
    const url = buildApiUrl(`/reviews/${reviewId}`);
    const response = await fetchWithTokenRefresh(url, {
      method: 'DELETE',
    });

    if (!response.ok) {
      throw await handleApiError(response, 'Failed to delete review');
    }

    return response.json();
  },
};
